import React, { useCallback, useMemo } from "react";
import moment from "moment";
import { DatePicker } from "@mantine/dates";
import {
  Badge,
  NumberInput,
  Select,
  Switch,
  Textarea,
  TextInput,
} from "@mantine/core";
import { IconEye, IconPencil } from "@tabler/icons";
import { showNotification } from "@mantine/notifications";
import ActionCellRender from "../../components/actionCellRender";
import DebitNotePaymentMatchingList from "../../components/debitNotePaymentMatchingList";
import FileList from "../../components/fileList";
import {
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
  PageList,
} from "../../components/pageList";
import RemoteSelect2 from "../../components/remoteSelect2";
import { DATA_CLIENT_PAYMENT_METHOD } from "../../data/options";
import {
  assertClientPaymentHasAssignments,
  clientPaymentApiError,
} from "../../helpers/clientPaymentMatching";
import { useCellRender } from "../../hooks/useCellRender";
import { useFormatter } from "../../hooks/useFomatter";
import { useServerApi } from "../../hooks/userServerApi";

const apiEntity = "clientPayment";

const ADJUSTMENT_TYPES = [
  "none",
  "Bank Charge",
  "Payment Amount Adjustment",
  "Commission",
  "A/P - Referral fee",
  "Temporary Receipt for Pending Policy",
  "Other",
];

const paymentClientId = (values) => {
  const fromClient = values?.fromClient;
  if (fromClient) return fromClient._id ?? fromClient;
  const client = values?.debitNotes?.[0]?.client;
  if (!client) return undefined;
  return client._id ?? client;
};

const ClientPaymentDebitNotes = (props) => (
  <DebitNotePaymentMatchingList
    {...props}
    clientId={paymentClientId(props.form?.values)}
    mode="edit"
  />
);

const ClientPayment = () => {
  const [api] = useServerApi();
  const [cellRender] = useCellRender();
  const [formatter] = useFormatter();

  const handleReceiptAutoSave = useCallback(
    async (updatedFiles, form) => {
      if (!form?.values?._id) return;
      try {
        assertClientPaymentHasAssignments(form.values);
        await api.ClientPayment.update(form.values._id, {
          ...form.values,
          files: updatedFiles,
        });
      } catch (error) {
        showNotification({
          color: "red",
          title: "Error",
          message: clientPaymentApiError(error),
        });
      }
    },
    [api]
  );

  const onSubmit = useCallback(
    async ({ values, formMode }) => {
      if (formMode !== "edit" || !values?._id) {
        return { success: false, error: "Invalid save request" };
      }
      try {
        assertClientPaymentHasAssignments(values);
        const data = await api.ClientPayment.update(values._id, values);
        return { success: true, data };
      } catch (error) {
        return { success: false, error: clientPaymentApiError(error) };
      }
    },
    [api]
  );

  const formSchema = useMemo(
    () => ({
      title: "Client Payment",
      titleField: "code",
      apiEntity,
      display: {
        mode: ENUM_FORM_DISPLAY.LINK,
      },
      initialValues: {},
      showSaveBar: true,
      validate: {
        amount: (value) => (value ? null : "Payment Amount is required"),
        date: (value) => (value ? null : "Payment Date is required"),
        files: (value) => (value ? null : "Receipt File is required"),
        bankAccount: (value, formValues) => {
          if (formValues?.directToProvider) return null;
          if (!value) return "Bank Account is required";
          return null;
        },
      },
      layout: {
        containers: [
          {
            key: "t1",
            parent: null,
            type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
            props: {
              variant: "default",
              defaultValue: "Payment Info",
            },
            tab: [
              { key: "t1", label: "Payment Info" },
              { key: "tab-multiple", label: "Multiple Debit Notes" },
            ],
          },
        ],
        fields: [
          {
            name: "code",
            component: TextInput,
            parent: "t1",
            props: {
              label: "Code",
              disabled: true,
            },
          },
          {
            name: "status",
            component: TextInput,
            parent: "t1",
            props: {
              label: "Status",
              disabled: true,
            },
          },
          {
            name: "date",
            component: DatePicker,
            parent: "t1",
            type: "datePicker",
            props: {
              label: "Payment Date",
              required: true,
              clearable: true,
              inputFormat: "YYYY-MM-DD",
              allowFreeInput: true,
            },
          },
          {
            name: "amount",
            component: NumberInput,
            parent: "t1",
            props: {
              label: "Payment Amount",
              parser: formatter.currencyParser,
              formatter: formatter.currencyFormat,
              precision: 2,
            },
          },
          {
            name: "adjustmentType",
            component: Select,
            parent: "t1",
            props: {
              label: "Adjustment Type",
              data: ADJUSTMENT_TYPES,
              searchable: true,
              clearable: true,
              defaultValue: "none",
            },
          },
          {
            name: "adjustmentAmount",
            component: NumberInput,
            parent: "t1",
            props: {
              label: "Adjustment Amount",
              parser: formatter.currencyParser,
              formatter: formatter.currencyFormat,
              precision: 2,
              defaultValue: 0,
            },
          },
          {
            name: "paymentMethod",
            component: Select,
            parent: "t1",
            props: {
              label: "Payment Method",
              data: DATA_CLIENT_PAYMENT_METHOD,
              searchable: true,
              clearable: true,
            },
          },
          {
            name: "chequeNumber",
            component: TextInput,
            parent: "t1",
            visibleOnly: (values) => values.paymentMethod === "Cheque",
            props: {
              label: "Cheque Number",
            },
          },
          {
            name: "directToProvider",
            parent: "t1",
            component: Switch,
            props: {
              type: "checkbox",
              size: "md",
              label: "Direct to Provider",
              mt: "md",
            },
          },
          {
            name: "bankAccount",
            component: RemoteSelect2,
            parent: "t1",
            visibleOnly: (values) => !values.directToProvider,
            props: {
              label: "Bank Account",
              apiEntity: "bankAccount",
              required: (values) => !values.directToProvider,
              placeholder: "Bank Account",
              valueField: "_id",
              labelField: "name",
              labelRender: (data) =>
                ` ${data?.shortName ?? ""} : ${data?.accountNumber ?? ""} `,
              multiple: false,
              searchFields: ["name", "code", "cname"],
              clearable: true,
              sort: { by: "bank", order: "asc" },
            },
          },
          {
            name: "matchedDate",
            parent: "t1",
            component: DatePicker,
            type: "datePicker",
            props: {
              label: "Match Date",
              zIndex: 1000,
              placeholder: "Pick Date",
              allowFreeInput: true,
            },
          },
          {
            name: "files",
            component: FileList,
            parent: "t1",
            props: {
              title: "Receipt File",
              tableMarginBottom: 10,
              showNoFilesMessage: false,
              defaultDisplayMode: 1,
              onAutoSave: handleReceiptAutoSave,
            },
          },
          {
            name: "remark",
            parent: "t1",
            component: Textarea,
            props: {
              label: "Remark",
            },
          },
          {
            name: "debitNotes",
            parent: "tab-multiple",
            component: ClientPaymentDebitNotes,
            props: {
              label: "Debit Notes",
            },
          },
        ],
      },
    }),
    [formatter.currencyFormat, formatter.currencyParser, handleReceiptAutoSave]
  );

  const tableSchema = useMemo(
    () => ({
      showActionColumn: true,
      columns: [
        { field: "code", headerName: "Code", sortable: true },
        {
          field: "date",
          headerName: "Payment Date",
          sortable: true,
          cellRender: (col, data) =>
            data.date ? moment(data.date).format("YYYY-MM-DD") : "",
        },
        {
          field: "amount",
          headerName: "Amount",
          sortable: true,
          cellRender: (col, data) => cellRender.Currency(data.amount),
        },
        {
          field: "status",
          headerName: "Status",
          sortable: true,
          cellRender: (col, data) => (
            <Badge size="sm" variant="outline">
              {data.status}
            </Badge>
          ),
        },
        { field: "paymentMethod", headerName: "Method", sortable: true },
        {
          field: "debitNotes",
          headerName: "Debit Notes",
          cellRender: (col, data) =>
            (data.debitNotes ?? [])
              .map((note) => note?.code)
              .filter(Boolean)
              .join(", "),
        },
        {
          field: "action",
          cellRender: ActionCellRender,
          isAction: true,
          actions: [
            { name: "edit", label: "Edit", icon: IconPencil },
            { name: "show", label: "View", icon: IconEye },
          ],
        },
      ],
      searchableFields: ["code", "status", "paymentMethod", "remark"],
    }),
    [cellRender]
  );

  return (
    <PageList
      title="Client Payments"
      apiEntity={apiEntity}
      tableSchema={tableSchema}
      formSchema={formSchema}
      showAddButton={false}
      onSubmit={onSubmit}
    />
  );
};

export default ClientPayment;
